/**
 * How a pinned provider version compares with the registry — as ONE predicate, in one place.
 *
 * The inputs look like they answer a single question and do not. `latest_matching` is the newest
 * version satisfying the lock file's own constraint; `latest_version` is the newest version that
 * exists, constraint ignored. And a null `latest_matching` has three distinct causes that an
 * operator must be able to tell apart:
 *
 *   - the checker ran and found nothing newer within the constraint  -> the pin is fine
 *   - the checker has never succeeded and last failed                -> we simply do not know
 *   - the checker has not reached this provider yet                  -> not an answer, yet
 *
 * Rendering the second as the first tells someone their pin is current when it has never been
 * compared against anything. That is the failure this module exists to make impossible to write
 * twice: the state table and the graph's edge modal both ask the question, and two copies of a
 * five-branch predicate is exactly how one amber badge comes to mean two things on two pages.
 */

export type ProviderFreshness =
  /** A newer version satisfies the constraint. `latest` is what to move to. */
  | { state: 'outdated'; latest: string }
  /** Checked, and the pin is the newest the constraint allows. */
  | { state: 'current' }
  /** Checked, and nothing newer satisfies the constraint. */
  | { state: 'none_newer'; checkedAt: number }
  /** Never successfully checked, and the last lookup failed. NOT the same as `current`. */
  | { state: 'unavailable'; failedAt: number }
  /** No successful check and no recorded failure: the checker has not got here yet. */
  | { state: 'pending' }

export interface ProviderFreshnessInput {
  /** The pinned version. `workspace_providers.current_version`. */
  current_version: string
  latest_matching?: string | null
  latest_version_checked_at?: number | null
  latest_lookup_error_at?: number | null
}

/**
 * Whether the upstream answer can be trusted AT ALL, independent of what it said.
 *
 * Split out because the two questions are genuinely different and get asked by different
 * surfaces. The state table's "latest available" column is about `latest_version` — the newest
 * version that exists — and must not consult `latest_matching`, which is scoped by the lock
 * file's constraint. Both still need to tell "we checked" from "we never got an answer", and
 * that part is this function.
 */
export type ProviderLookupState = 'ok' | 'unavailable' | 'pending'

export function providerLookupState(p: ProviderFreshnessInput): ProviderLookupState {
  // A success always wins over a stale error: error_at records the last failure, not the
  // current state, so a provider that failed yesterday and succeeded today is 'ok'.
  if (p.latest_version_checked_at) return 'ok'
  if (p.latest_lookup_error_at) return 'unavailable'
  return 'pending'
}

export function providerFreshness(p: ProviderFreshnessInput): ProviderFreshness {
  // Same comparison the worker runs to set the provider node's `outdated` flag (orgGraph.ts).
  // If that rule changes, this changes with it.
  if (p.latest_matching) {
    return p.latest_matching === p.current_version
      ? { state: 'current' }
      : { state: 'outdated', latest: p.latest_matching }
  }
  switch (providerLookupState(p)) {
    case 'ok': return { state: 'none_newer', checkedAt: p.latest_version_checked_at! }
    case 'unavailable': return { state: 'unavailable', failedAt: p.latest_lookup_error_at! }
    case 'pending': return { state: 'pending' }
  }
}

/** Whether this freshness should read as a warning. `pending` and `none_newer` are not. */
export function isFreshnessWarning(f: ProviderFreshness): boolean {
  return f.state === 'outdated' || f.state === 'unavailable'
}
